/**
 * Hooks TanStack Query para as telas administrativas: painel (`/dashboard`),
 * usuários (`/users`), estados/alíquotas (`/states`) e ajustes (`/settings`).
 *
 * Cada mutação invalida (ou substitui) a query correspondente ao terminar.
 * O tratamento de erro — toast, `ApiError.fields` em formulário — fica a
 * cargo de quem chama `mutateAsync`, igual ao padrão já usado em
 * `pages/Perfil.tsx`. Isto mantém os hooks só sobre dados, não sobre UI.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post, put } from '@/lib/api';
import type {
  BankSettings,
  CompanySettings,
  CreateStateInput,
  CreateUserInput,
  DashboardData,
  ProposalDefaultsSettings,
  Settings,
  StateTax,
  UpdateStateInput,
  UpdateUserInput,
  User,
} from '@/lib/types';

// ------------------------------------------------------------------ painel --

/** `GET /dashboard` — métricas do mês, últimas propostas, top produtos, reajustes e agenda de preços. */
export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: () => get<DashboardData>('/dashboard'),
  });
}

// --------------------------------------------------------------- usuários --

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => (await get<{ data: User[] }>('/users')).data,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateUserInput) => (await post<{ data: User }>('/users', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: number; input: UpdateUserInput }) =>
      (await patch<{ data: User }>(`/users/${id}`, input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => del<{ ok: true }>(`/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

// ----------------------------------------------------------------- estados --

export function useStates() {
  return useQuery({
    queryKey: ['states'],
    queryFn: async () => (await get<{ data: StateTax[] }>('/states')).data,
  });
}

export function useCreateState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStateInput) => post<{ data: StateTax }>('/states', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['states'] }),
  });
}

export function useUpdateState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uf, input }: { uf: string; input: UpdateStateInput }) =>
      patch<{ data: StateTax }>(`/states/${uf}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['states'] }),
  });
}

export function useDeleteState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uf: string) => del<void>(`/states/${uf}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['states'] }),
  });
}

// ----------------------------------------------------------------- ajustes --

/** `GET /settings` — as três chaves de ajuste de uma vez: `{ company, bank, proposalDefaults }`. */
export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () => get<Settings>('/settings'),
  });
}

export function useUpdateCompanySettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: CompanySettings) => put<Settings>('/settings/company', value),
    onSuccess: (data) => qc.setQueryData<Settings>(['settings'], data),
  });
}

export function useUpdateBankSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: BankSettings) => put<Settings>('/settings/bank', value),
    onSuccess: (data) => qc.setQueryData<Settings>(['settings'], data),
  });
}

export function useUpdateProposalDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: ProposalDefaultsSettings) => put<Settings>('/settings/proposalDefaults', value),
    onSuccess: (data) => qc.setQueryData<Settings>(['settings'], data),
  });
}
